/**
 * ImproveWebsiteCTA — the AI Site Builder entry point on the report page.
 *
 * It first asks the backend whether this business already has a builder
 * (GET /site-builder/businesses/:id/builder). Each business owns exactly ONE active
 * builder:
 *   - No builder yet → the marketing "Generate your website" card (first generation).
 *   - Builder exists → a management card ("Your website is ready") that opens the
 *     existing builder / live preview, or regenerates it IN PLACE (never creates a
 *     second website).
 *
 * Pure presentation + the generation hook — no generation logic lives here.
 */
import { motion } from 'framer-motion'
import {
  Sparkles, Wand2, ArrowRight, Zap, LayoutTemplate, Palette,
  Loader2, ExternalLink, RotateCw, CheckCircle2, Clock,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import type { ReportPayload } from '../../types'
import { siteBuilderApi } from '../../api/client'
import type { BuilderRecord } from '../../api/client'
import { BRAND } from '../../config'
import { useI18nNavigate } from '../../hooks/useI18nNavigate'
import { useSiteGeneration } from '../hooks/useSiteGeneration'
import { SiteGenerationOverlay } from './SiteGenerationOverlay'

const FEATURE_ICONS = [Zap, LayoutTemplate, Palette] as const
const FEATURE_KEYS = ['instant', 'tailored', 'branded'] as const

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
      className="mb-8 overflow-hidden rounded-2xl border"
      style={{ borderColor: `${BRAND.navy}20` }}
    >
      {children}
    </motion.div>
  )
}

function Header({ title, badge, subtitle }: { title: string; badge: string; subtitle: string }) {
  return (
    <div className="px-6 py-5" style={{ background: `linear-gradient(135deg, ${BRAND.navy} 0%, #03224a 100%)` }}>
      <div className="flex items-center gap-3">
        <div
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-white"
          style={{ background: `linear-gradient(135deg, ${BRAND.red}, ${BRAND.navyLight})` }}
        >
          <Sparkles className="h-5 w-5" />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-base font-bold text-white">{title}</h3>
            <span
              className="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white"
              style={{ backgroundColor: BRAND.red }}
            >
              {badge}
            </span>
          </div>
          <p className="text-xs text-gray-300">{subtitle}</p>
        </div>
      </div>
    </div>
  )
}

export function ImproveWebsiteCTA({ report }: { report: ReportPayload }) {
  const { t } = useTranslation('builder')
  const navigate = useI18nNavigate()
  const { status, stepIndex, progress, start } = useSiteGeneration(report)
  const isGenerating = status === 'generating'

  const { data: builder, isLoading } = useQuery({
    queryKey: ['business-builder', report.business_id],
    queryFn: () => siteBuilderApi.getBuilderForBusiness(report.business_id),
    staleTime: 0,
    retry: false,
  })

  const overlay = (
    <SiteGenerationOverlay open={isGenerating} stepIndex={stepIndex} progress={progress} businessName={report.business_name} />
  )

  // While checking existence, show a compact loading card (avoids flashing the wrong CTA).
  if (isLoading) {
    return (
      <Shell>
        <Header title={t('cta.title')} badge={t('cta.badge')} subtitle={t('cta.subtitle')} />
        <div className="flex items-center gap-2 bg-white p-6 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t('builder.checking', 'Checking your website…')}
        </div>
      </Shell>
    )
  }

  if (builder && builder.status === 'succeeded') {
    return (
      <>
        <ManagementCard
          builder={builder}
          businessName={report.business_name}
          onOpen={() => navigate(`/builder/${builder.id}`, { state: { scanId: report.scan_id } })}
          onLive={() => window.open(`/builder/${builder.id}/live`, '_blank', 'noopener,noreferrer')}
          onRegenerate={() => start({ regenerate: true })}
          disabled={isGenerating}
        />
        {overlay}
      </>
    )
  }

  return (
    <>
      <MarketingCard report={report} onGenerate={() => start()} disabled={isGenerating} />
      {overlay}
    </>
  )
}

// ── Management card (builder exists) ──────────────────────────────────────────

function ManagementCard({
  builder,
  businessName,
  onOpen,
  onLive,
  onRegenerate,
  disabled,
}: {
  builder: BuilderRecord
  businessName: string
  onOpen: () => void
  onLive: () => void
  onRegenerate: () => void
  disabled: boolean
}) {
  const { t } = useTranslation('builder')
  const designSystem = builder.schema?.theme.designSystem ?? '—'
  const providerLabel = builder.provider === 'mock' ? 'Mock' : 'Live'
  const updated = safeDate(builder.updatedAt || builder.createdAt)

  const meta: { label: string; value: string }[] = [
    { label: t('builder.meta.business', 'Business'), value: businessName },
    { label: t('builder.meta.design', 'Design system'), value: capitalize(designSystem) },
    { label: t('builder.meta.provider', 'Provider'), value: providerLabel },
    { label: t('builder.meta.updated', 'Last updated'), value: updated },
  ]

  return (
    <Shell>
      <Header
        title={t('builder.ready.title', 'Your website is ready')}
        badge={t('cta.badge')}
        subtitle={t('builder.ready.subtitle', 'Preview, open, or regenerate your AI-built website.')}
      />
      <div className="bg-white p-6">
        <div className="mb-5 flex items-center gap-2 text-sm font-medium text-emerald-600">
          <CheckCircle2 className="h-4 w-4" />
          {t('builder.ready.status', 'Website generated and saved')}
        </div>

        <dl className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
          {meta.map((m) => (
            <div key={m.label} className="rounded-xl border border-gray-100 bg-gray-50/50 p-3">
              <dt className="mb-0.5 flex items-center gap-1 text-[11px] font-medium uppercase tracking-wide text-gray-400">
                {m.label === t('builder.meta.updated', 'Last updated') ? <Clock className="h-3 w-3" /> : null}
                {m.label}
              </dt>
              <dd className="truncate text-sm font-semibold text-gray-900">{m.value}</dd>
            </div>
          ))}
        </dl>

        <div className="flex flex-col gap-3 border-t border-gray-100 pt-4 sm:flex-row sm:items-center">
          <button
            type="button"
            onClick={onOpen}
            className="flex flex-1 items-center justify-center gap-2 rounded-xl px-6 py-3 text-sm font-semibold text-white shadow-sm transition-colors"
            style={{ backgroundColor: BRAND.red }}
            onMouseOver={(e) => { e.currentTarget.style.backgroundColor = BRAND.redHover }}
            onMouseOut={(e) => { e.currentTarget.style.backgroundColor = BRAND.red }}
          >
            <Wand2 className="h-4 w-4" />
            {t('builder.open', 'Open website builder')}
            <ArrowRight className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={onLive}
            className="flex items-center justify-center gap-2 rounded-xl border border-gray-300 px-5 py-3 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50"
          >
            <ExternalLink className="h-4 w-4" />
            {t('builder.livePreview', 'Live preview')}
          </button>
          <button
            type="button"
            onClick={onRegenerate}
            disabled={disabled}
            className="flex items-center justify-center gap-2 rounded-xl border border-gray-300 px-5 py-3 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-60"
          >
            <RotateCw className="h-4 w-4" />
            {t('builder.regenerate', 'Regenerate')}
          </button>
        </div>
      </div>
    </Shell>
  )
}

// ── Marketing card (no builder yet — unchanged flow) ──────────────────────────

function MarketingCard({ report, onGenerate, disabled }: { report: ReportPayload; onGenerate: () => void; disabled: boolean }) {
  const { t } = useTranslation('builder')
  return (
    <Shell>
      <Header title={t('cta.title')} badge={t('cta.badge')} subtitle={t('cta.subtitle')} />
      <div className="bg-white p-6">
        <p className="mb-5 text-sm leading-relaxed text-gray-600">
          {t('cta.description', { businessName: report.business_name })}
        </p>

        <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
          {FEATURE_KEYS.map((key, i) => {
            const Icon = FEATURE_ICONS[i]
            return (
              <div key={key} className="flex items-start gap-3 rounded-xl border border-gray-100 bg-gray-50/50 p-3">
                <div
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-white"
                  style={{ backgroundColor: BRAND.navy }}
                >
                  <Icon className="h-5 w-5" />
                </div>
                <div>
                  <p className="mb-0.5 text-sm font-semibold text-gray-900">{t(`cta.features.${key}.title`)}</p>
                  <p className="text-xs leading-relaxed text-gray-500">{t(`cta.features.${key}.desc`)}</p>
                </div>
              </div>
            )
          })}
        </div>

        <div className="flex flex-col items-center gap-4 border-t border-gray-100 pt-4 sm:flex-row">
          <div className="flex-1">
            <p className="mb-0.5 text-sm font-semibold text-gray-900">{t('cta.ready')}</p>
            <p className="text-xs text-gray-500">{t('cta.readySubtitle')}</p>
          </div>
          <button
            type="button"
            onClick={onGenerate}
            disabled={disabled}
            className="flex shrink-0 items-center justify-center gap-2 rounded-xl px-6 py-3 text-sm font-semibold text-white shadow-sm transition-colors max-sm:w-full"
            style={{ backgroundColor: BRAND.red }}
            onMouseOver={(e) => { e.currentTarget.style.backgroundColor = BRAND.redHover }}
            onMouseOut={(e) => { e.currentTarget.style.backgroundColor = BRAND.red }}
          >
            <Wand2 className="h-4 w-4" />
            {t('cta.button')}
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </Shell>
  )
}

function capitalize(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s
}

function safeDate(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString()
}
